import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { FontVariantToken } from '@uipath/apollo-core';
import React from 'react';

/**
 * Returns the horizontal padding for a wizard step based on the form type.
 */
const getWizardHorizontalPadding = (type: 'add' | 'edit') =>
    type === 'edit' ? 'clamp(24px, 3%, 48px)' : '48px';

/**
 * Shared outer container for governance wizard steps (padding + full height).
 */
export const WizardStepContainer: React.FC<{
    type: 'add' | 'edit';
    children: React.ReactNode;
    'data-cy'?: string;
}> = ({
    type, children, 'data-cy': dataCy,
}) => {
    const horizontalPadding = getWizardHorizontalPadding(type);
    return (
        <UiStack
            direction="column"
            pt="24px"
            pl={horizontalPadding}
            pr={horizontalPadding}
            h="100%"
            data-cy={dataCy}
        >
            {children}
        </UiStack>
    );
};

/**
 * Shared header block for governance wizard steps (title + description).
 */
export const WizardStepHeader: React.FC<{
    title: string;
    description: string;
    titleVariant?: FontVariantToken;
}> = ({
    title,
    description,
    titleVariant = FontVariantToken.fontSizeH3Bold,
}) => (
    <UiStack
        direction="column"
        gap={SpacingToken.S}
        mb={SpacingToken.M}
        data-cy="wizard-step-header"
    >
        <UiText
            variant={titleVariant}
            data-cy="wizard-step-title"
        >
            {title}
        </UiText>
        <UiText
            variant={FontVariantToken.fontSizeM}
            color="var(--color-foreground-de-emp)"
            data-cy="wizard-step-description"
        >
            {description}
        </UiText>
    </UiStack>
);
